import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import type {
  ResourceCalendarSettings,
  ResourceCalendarSettingsInput,
} from '@/types/resource-schedule-api';
import {
  resourceScheduleQueryKey,
  resourceScheduleUrl,
  toResourceScheduleError,
} from './use-resource-schedule';

/**
 * Every Schedule write opts out of the global `MutationCache.onError` toast
 * (`query-client.ts`): the section owns the messaging — a 400 is shown inline,
 * a 409 flips the tab read-only, a 404 closes it — so the generic "Something
 * went wrong" must not stack on top. Same opt-out as `use-save-resource.ts`.
 */
export const SCHEDULE_OWN_ERROR_HANDLING = { skipGlobalError: true } as const;

/**
 * Replace the device's calendar settings — PROD-2007.
 *
 *   PUT /api/Resource/{resourceId}/Schedule/CalendarSettings
 *   body: { defaultCalendarIncrement, showAvailableSlotsToPatientDaysAhead,
 *           showAvailableSlotsToPracticeDaysAhead, overBookingPerSlot }
 *
 * Full replace: all four are required on every call. Bounds (increment
 * 1..240, both days-ahead 0..730, overbooking 0..10) are pre-validated by
 * the dialog; the server remains the authority.
 */
export function useSaveResourceCalendarSettings(resourceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ResourceCalendarSettingsInput) => {
      try {
        return await put<ResourceCalendarSettings>(
          `${resourceScheduleUrl(resourceId)}/CalendarSettings`,
          input,
        );
      } catch (err) {
        throw toResourceScheduleError(err);
      }
    },
    meta: SCHEDULE_OWN_ERROR_HANDLING,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceScheduleQueryKey(resourceId) });
    },
  });
}
